/**
 * The tracked aircraft icon in the browser, the same drawing as Teslyn Field's (`apps/field/src/map/aircraftIcon.ts`;
 * Diurnal parity 03-022; brand book, Iconography): a plane, a helicopter (rotor disc) or a vehicle seen from above,
 * nose up, turned to the heading (`aircraftGlyph`), drawn in the colour token the domain gives (`aircraftColours`:
 * the tracker source's colour, decision 280; `fail` for SOS and lost tracking; grey for no recent position, decision
 * 326). An RFDS aircraft is a white aircraft on the aeromedical disc (decisions 284 and 320). The markup holds only
 * fixed shapes, a number and a token name: no text from a tracker goes in it.
 */
import {
  aircraftGlyph,
  type AircraftColours,
  type AircraftGlyph,
  type AircraftPosition,
} from '@teslyn/domain';

const SHAPES: Readonly<Record<AircraftGlyph, string>> = {
  plane:
    '<path d="M12 2 L13.4 9 L22 13 L22 14.6 L13.4 12.4 L13 19 L16 21 L16 22 L12 21 L8 22 L8 21 L11 19 L10.6 12.4 L2 14.6 L2 13 L10.6 9 Z"/>',
  helicopter:
    '<circle cx="12" cy="9" r="7.5" fill="none" stroke="currentColor" stroke-width="1"/><path d="M12 4 C14.2 4 15.2 6 15.2 9 C15.2 12 14 13.6 12.8 14 L12.6 21 L15 22 L9 22 L11.4 21 L11.2 14 C10 13.6 8.8 12 8.8 9 C8.8 6 9.8 4 12 4 Z"/>',
  vehicle:
    '<path d="M8 3 L16 3 C17 3 17.5 3.8 17.5 5 L17.5 20 C17.5 21 17 21.5 16 21.5 L8 21.5 C7 21.5 6.5 21 6.5 20 L6.5 5 C6.5 3.8 7 3 8 3 Z M8 6 L16 6 L15.4 9 L8.6 9 Z"/>',
};

const AEROMEDICAL_DISC = '<circle class="map-aircraft-icon__disc" cx="12" cy="12" r="11.25"/>';

const onDisc = (shape: string): string =>
  `<g class="map-aircraft-icon__on-disc" transform="translate(12 12) scale(0.7) translate(-12 -12)">${shape}</g>`;

export function aircraftIcon(
  a: Pick<AircraftPosition, 'kind' | 'heading_deg' | 'airborne' | 'state' | 'rfds'>,
  colour: AircraftColours['icon'] = 'accent',
): { readonly html: string; readonly className: string } {
  const g = aircraftGlyph(a);
  const className = [
    'map-aircraft-icon',
    `map-aircraft-icon--${g.glyph}`,
    g.filled ? 'map-aircraft-icon--airborne' : 'map-aircraft-icon--ground',
    g.alarm ? 'map-aircraft-icon--alarm' : '',
    g.aeromedical ? 'map-aircraft-icon--aeromedical' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const token = g.alarm ? 'fail' : colour;
  const shape = g.aeromedical ? onDisc(SHAPES[g.glyph]) : SHAPES[g.glyph];
  const html =
    `<svg viewBox="0 0 24 24" aria-hidden="true" style="color: var(--${token})">` +
    `${g.aeromedical ? AEROMEDICAL_DISC : ''}` +
    `<g style="transform: rotate(${g.rotationDeg}deg); transform-origin: 12px 12px">${shape}</g></svg>`;
  return { html, className };
}
